import { create } from 'zustand';
import api from '../config/axios';
import { ACTIVITIES } from '../config/apiPaths';
import { notifyEntityCreated, notifyEntityUpdated } from '../utils/entityNotifications';
import type { ActivityType, IActivity } from '../types/activity';
import { defaultActivityTypes } from '../types/activity';
import { generateRecurringInstances } from '../utils/recurrence';

export type { IActivity }; // Re-export IActivity type

export type ActivityCreate = Omit<IActivity, 'id' | 'created_at' | 'updated_at' | 'deleted_at'>;
export type ActivityUpdate = Partial<ActivityCreate>;

interface ActivityStore {
  activities: IActivity[];
  currentActivity: IActivity | null;
  activityTypes: ActivityType[];
  isLoading: boolean;
  error: string | null;

  fetchActivities: () => Promise<void>;
  fetchActivityById: (id: number) => Promise<void>;
  createActivity: (activityData: ActivityCreate) => Promise<IActivity>;
  updateActivity: (id: number, event: ActivityUpdate) => Promise<void>;
  deleteActivity: (id: number) => Promise<void>;
  fetchActivityTypes: () => Promise<void>;

  getActivityById: (id: number) => IActivity | undefined;
  getActivityTypeById: (id: number) => ActivityType | undefined;
  clearError: () => void;

  createRecurringActivity: (activityData: ActivityCreate) => Promise<IActivity>;
  updateRecurringActivity: (id: number, updates: Partial<IActivity>, updateType: 'this' | 'future' | 'all') => Promise<IActivity>;
  deleteRecurringActivity: (id: number, deleteType: 'this' | 'future' | 'all') => Promise<boolean>;
}

export const useActivityStore = create<ActivityStore>((set, get) => ({
  activities: [],
  currentActivity: null,
  activityTypes: defaultActivityTypes,
  isLoading: false,
  error: null,

  fetchActivities: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get(ACTIVITIES.ALL);
      
      // Check if the response has a data property (for paginated responses)
      const activitiesData = response.data.data || response.data;
      
      // If activitiesData is an array, use it directly, otherwise use an empty array
      const activities = Array.isArray(activitiesData) ? activitiesData : [];
      
      set({ activities, isLoading: false });
    } catch (error: any) {
      console.error('Error fetching activities:', error);
      const errorMessage = error.response?.data?.message || 'Failed to fetch activities';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  fetchActivityById: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      console.log(`Fetching activity with ID: ${id}`);
      const response = await api.get(ACTIVITIES.BY_ID(id));
      
      // Check if the response has a data property (for consistency)
      const fetchedActivity = response.data.data || response.data;
      
      console.log('Activity fetched successfully:', fetchedActivity);
      
      set((state) => ({
        activities: [...state.activities.filter(e => e.id !== id), fetchedActivity],
        currentActivity: fetchedActivity,
        isLoading: false
      }));
      
      return fetchedActivity;
    } catch (error: any) {
      console.error(`Error fetching activity ${id}:`, error);
      const errorMessage = error.response?.data?.message || 'Failed to fetch activity';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  createActivity: async (activityData: ActivityCreate) => {
    set({ isLoading: true, error: null });
    try {
      console.log('Creating activity:', activityData);
      
      // Use the same simple approach as notes - minimal transformation
      const createPayload = { ...activityData };
      
      // Convert string fields to numbers where needed (same as notes)
      if ('proposal_id' in createPayload && createPayload.proposal_id !== undefined && createPayload.proposal_id !== null) {
        createPayload.proposal_id = Number(createPayload.proposal_id);
      }
      if ('status_id' in createPayload && createPayload.status_id !== undefined && createPayload.status_id !== null) {
        createPayload.status_id = Number(createPayload.status_id);
      }
      if ('type_id' in createPayload && createPayload.type_id !== undefined && createPayload.type_id !== null) {
        createPayload.type_id = Number(createPayload.type_id);
      }
      if ('assignee_id' in createPayload && createPayload.assignee_id !== undefined && createPayload.assignee_id !== null) {
        createPayload.assignee_id = Number(createPayload.assignee_id);
      }
       
      console.log('Final create payload:', createPayload);
      const response = await api.post(ACTIVITIES.ALL, createPayload);
      
      // Check if the response has a data property (for consistency)
      const createdActivity = response.data.data || response.data;
      
      console.log('Activity created successfully:', createdActivity);
      
      set((state) => ({
        activities: [...state.activities, createdActivity],
        isLoading: false
      }));
      
      // Notify about the new activity creation
      notifyEntityCreated('activity', {
        id: createdActivity.id,
        title: createdActivity.title,
      });
      
      return createdActivity;
    } catch (error: any) {
      console.error('Error creating activity:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create activity';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  updateActivity: async (id: number, activityUpdate: ActivityUpdate) => {
    set({ isLoading: true, error: null });
    try {
      console.log(`Updating activity with ID ${id}:`, activityUpdate);
      
      // Use the same simple approach as notes - minimal transformation
      const updatePayload = { ...activityUpdate };
      
      // Convert string fields to numbers where needed (same as notes)
      if ('proposal_id' in updatePayload && updatePayload.proposal_id !== undefined && updatePayload.proposal_id !== null) {
        updatePayload.proposal_id = Number(updatePayload.proposal_id);
      }
      if ('status_id' in updatePayload && updatePayload.status_id !== undefined && updatePayload.status_id !== null) {
        updatePayload.status_id = Number(updatePayload.status_id);
      }
      if ('type_id' in updatePayload && updatePayload.type_id !== undefined && updatePayload.type_id !== null) {
        updatePayload.type_id = Number(updatePayload.type_id);
      }
      if ('assignee_id' in updatePayload && updatePayload.assignee_id !== undefined && updatePayload.assignee_id !== null) {
        updatePayload.assignee_id = Number(updatePayload.assignee_id);
      }
      
      // Handle participants - convert to array of strings
      if ('participants' in updatePayload && updatePayload.participants !== undefined && updatePayload.participants !== null) {
        updatePayload.participants = Array.isArray(updatePayload.participants) 
          ? updatePayload.participants.map(String)
          : [String(updatePayload.participants)];
      }
      
      
      console.log('Final update payload:', updatePayload);
      const response = await api.put(ACTIVITIES.BY_ID(id), updatePayload);
      
      // Check if the response has a data property (for consistency)
      const updatedActivity = response.data.data || response.data;
      
      console.log('Activity updated successfully:', updatedActivity);
      
      set((state) => ({
        activities: state.activities.map(e => e.id === id ? updatedActivity : e),
        currentActivity: state.currentActivity?.id === id ? updatedActivity : state.currentActivity,
        isLoading: false
      }));
      
      // Notify about the activity update
      notifyEntityUpdated('activity', {
        id: updatedActivity.id,
        title: updatedActivity.title,
      });
      
      return updatedActivity;
    } catch (error: any) {
      console.error(`Error updating activity ${id}:`, error);
      const errorMessage = error.response?.data?.message || 'Failed to update activity';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  deleteActivity: async (id: number) => {
    set({ isLoading: true, error: null });

    try {
      console.log(`Deleting activity with ID: ${id} (type: ${typeof id})`);
      
      // First, verify the activity exists
      const activity = get().activities.find(a => a.id === id);
      if (!activity) {
        const errorMsg = `Activity with ID ${id} not found in local state`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      // Only use the URL path parameter approach since that's what the server supports
      const url = ACTIVITIES.BY_ID(id);
      console.log(`Sending DELETE request to: ${url}`);
      
      const response = await api.delete(url);
      console.log(`Activity with ID ${id} deleted successfully:`, response.data);
      
      // Update the local state
      set((state) => ({
        activities: state.activities.filter(e => e.id !== id),
        currentActivity: state.currentActivity?.id === id ? null : state.currentActivity,
        isLoading: false
      }));
      
      return response.data;
    } catch (error: any) {
      console.error(`Error deleting activity ${id}:`, error);
      const errorMessage = error.response?.data?.message || 'Failed to delete activity';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  fetchActivityTypes: async () => {
    // For now, just return the default types
    // In the future, this could fetch from an API endpoint
    set({ activityTypes: defaultActivityTypes });
  },

  getActivityById: (id: number) => get().activities.find(e => e.id === id),
  getActivityTypeById: (id: number) => get().activityTypes.find(type => type.id === id),
  clearError: () => set({ error: null }),

  createRecurringActivity: async (activityData: ActivityCreate) => {
    set({ isLoading: true, error: null });
    try {
      console.log('Creating recurring activity:', activityData);
      
      const createPayload = { ...activityData, is_recurring: true };
      if ('proposal_id' in createPayload && createPayload.proposal_id !== undefined && createPayload.proposal_id !== null) {
        createPayload.proposal_id = Number(createPayload.proposal_id);
      }
      if ('status_id' in createPayload && createPayload.status_id !== undefined && createPayload.status_id !== null) {
        createPayload.status_id = Number(createPayload.status_id);
      }
      
      const response = await api.post(ACTIVITIES.ALL, createPayload);
      const baseActivity = response.data.data || response.data;
      
      // Generate recurring instances
      const instances = generateRecurringInstances(baseActivity);
      
      // Create all instances in the backend
      const instancePromises = instances.map(instance => 
        api.post(ACTIVITIES.ALL, { ...instance, parent_id: baseActivity.id })
      );
      
      const instanceResponses = await Promise.all(instancePromises);
      const createdInstances = instanceResponses.map(res => res.data.data || res.data);
      
      set((state) => ({
        activities: [...state.activities, baseActivity, ...createdInstances],
        isLoading: false
      }));
      
      notifyEntityCreated('activity', {
        id: baseActivity.id,
        title: baseActivity.title,
      });
      
      return baseActivity;
    } catch (error: any) {
      console.error('Error creating recurring activity:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create recurring activity';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  updateRecurringActivity: async (id: number, updates: Partial<IActivity>, updateType: 'this' | 'future' | 'all') => {
    set({ isLoading: true, error: null });
    try {
      console.log(`Updating recurring activity with ID ${id}, type: ${updateType}`);
      
      const activity = get().activities.find(a => a.id === id);
      if (!activity) {
        throw new Error('Activity not found');
      }
      
      const updatePayload = { ...updates };
      if ('proposal_id' in updatePayload && updatePayload.proposal_id !== undefined && updatePayload.proposal_id !== null) {
        updatePayload.proposal_id = Number(updatePayload.proposal_id);
      }
      if ('status_id' in updatePayload && updatePayload.status_id !== undefined && updatePayload.status_id !== null) {
        updatePayload.status_id = Number(updatePayload.status_id);
      }
      
      let updatedActivities: IActivity[] = [];
      
      if (updateType === 'this') {
        // Update only this instance
        const response = await api.put(ACTIVITIES.BY_ID(id), updatePayload);
        const updatedActivity = response.data.data || response.data;
        updatedActivities = [updatedActivity];
      } else if (updateType === 'all') {
        // Update all instances in the series
        const baseId = activity.parent_id || activity.id;
        const instances = get().activities.filter(a => a.id === baseId || a.parent_id === baseId);
        
        const updatePromises = instances.map(instance => 
          api.put(ACTIVITIES.BY_ID(instance.id), updatePayload)
        );
        
        const responses = await Promise.all(updatePromises);
        updatedActivities = responses.map(res => res.data.data || res.data);
      } else if (updateType === 'future') {
        // Update this and all future instances
        const baseId = activity.parent_id || activity.id;
        const instances = get().activities.filter(a => 
          (a.id === baseId || a.parent_id === baseId) && 
          new Date(a.start_date) >= new Date(activity.start_date)
        );
        
        const updatePromises = instances.map(instance => 
          api.put(ACTIVITIES.BY_ID(instance.id), updatePayload)
        );
        
        const responses = await Promise.all(updatePromises);
        updatedActivities = responses.map(res => res.data.data || res.data);
      }
      
      // Update local state
      set((state) => ({
        activities: state.activities.map(a => {
          const updated = updatedActivities.find(ua => ua.id === a.id);
          return updated || a;
        }),
        currentActivity: state.currentActivity && updatedActivities.find(ua => ua.id === state.currentActivity!.id) || state.currentActivity,
        isLoading: false
      }));
      
      const mainUpdated = updatedActivities.find(ua => ua.id === id) || updatedActivities[0];
      
      notifyEntityUpdated('activity', {
        id: mainUpdated.id,
        title: mainUpdated.title,
      });
      
      return mainUpdated;
    } catch (error: any) {
      console.error(`Error updating recurring activity ${id}:`, error);
      const errorMessage = error.response?.data?.message || 'Failed to update recurring activity';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  deleteRecurringActivity: async (id: number, deleteType: 'this' | 'future' | 'all') => {
    set({ isLoading: true, error: null });
    try {
      console.log(`Deleting recurring activity with ID ${id}, type: ${deleteType}`);
      
      const activity = get().activities.find(a => a.id === id);
      if (!activity) {
        throw new Error('Activity not found');
      }
      
      let deletedIds: number[] = [];
      
      if (deleteType === 'this') {
        // Delete only this instance
        await api.delete(ACTIVITIES.BY_ID(id));
        deletedIds = [id];
      } else if (deleteType === 'all') {
        // Delete all instances in the series
        const baseId = activity.parent_id || activity.id;
        const instances = get().activities.filter(a => a.id === baseId || a.parent_id === baseId);
        
        const deletePromises = instances.map(instance => 
          api.delete(ACTIVITIES.BY_ID(instance.id))
        );
        
        await Promise.all(deletePromises);
        deletedIds = instances.map(i => i.id);
      } else if (deleteType === 'future') {
        // Delete this and all future instances
        const baseId = activity.parent_id || activity.id;
        const instances = get().activities.filter(a => 
          (a.id === baseId || a.parent_id === baseId) && 
          new Date(a.start_date) >= new Date(activity.start_date)
        );
        
        const deletePromises = instances.map(instance => 
          api.delete(ACTIVITIES.BY_ID(instance.id))
        );
        
        await Promise.all(deletePromises);
        deletedIds = instances.map(i => i.id);
      }
      
      // Update local state
      set((state) => ({
        activities: state.activities.filter(a => !deletedIds.includes(a.id)),
        currentActivity: state.currentActivity && deletedIds.includes(state.currentActivity.id) ? null : state.currentActivity,
        isLoading: false
      }));
      
      return true;
    } catch (error: any) {
      console.error(`Error deleting recurring activity ${id}:`, error);
      const errorMessage = error.response?.data?.message || 'Failed to delete recurring activity';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },
}));